import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage/LazyImage";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import * as cssLinter from "css";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    image = "image",
    mobileImage = "mobileImage",
    title = "title",
    heading = "heading",
    horizontalTextAlignment = "horizontalTextAlignment",
    verticalTextAlignment = "verticalTextAlignment",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    disableButton = "disableButton",
    mobileHorizontalAlignment = "mobileHorizontalAlignment",
    mobileVerticalAlignment = "mobileVerticalAlignment",
    buttonBackgroundColor = "buttonBackgroundColor",
    buttonTextColor = "buttonTextColor",
    opacity = "opacity",
    openInNewWindow = "openInNewWindow",
    cssClass = "cssClass",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.mobileImage]: string;
        [fields.title]: string;
        [fields.heading]: string;
        [fields.verticalTextAlignment]: "top" | "center" | "bottom";
        [fields.horizontalTextAlignment]: "left" | "center" | "right";
        [fields.buttonLabel]: string;
        [fields.disableButton]: boolean;
        [fields.buttonLink]: LinkFieldValue;
        [fields.openInNewWindow]: boolean;
        [fields.mobileHorizontalAlignment]: "left" | "center" | "right";
        [fields.mobileVerticalAlignment]: "top" | "center" | "bottom";
        [fields.buttonBackgroundColor]: string;
        [fields.buttonTextColor]: string;
        [fields.opacity]: string;
        [fields.cssClass]: string;
        [fields.customCSS]: string;
    };
}

export interface HeroBannerStyles {
    heroBannerImageContainer?: InjectableCss;
    heroBannerImage?: LazyImageProps;
    bannerButton?: InjectableCss;
    bannerText?: InjectableCss;
    wrapper?: InjectableCss;
    overlayWrapper?: InjectableCss;
    heading?: InjectableCss;
    TextLeft?: InjectableCss;
    TextRight?: InjectableCss;
    container?: InjectableCss;
}

export const heroBannerStyles: HeroBannerStyles = {
    heroBannerImageContainer: {
        css: css`
            cursor: pointer;
            display: flex;
            justify-content: center;
            align-items: center;
            text-align: center;
            position: relative;
        `,
    },
    heading: {
        css: css`
            > p {
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-weight: 600;
                font-size: 28px;
                line-height: 36px;
                color: ${getColor("primary.contrast")};
                text-transform: uppercase;
                margin-bottom: 0;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            font-weight: 700;
                            line-height: 34px;
                        `,
                        css`
                            font-weight: 700;
                            line-height: 34px;
                        `,
                        css`
                            font-weight: 700;
                            line-height: 34px;
                        `,
                        null,
                        null,
                    ])}
            }
        `,
    },
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
        `,
    },
    overlayWrapper: {
        css: css`
            width: 100%;
            color: white;
            text-align: center;
        `,
    },
    container: {
        css: css`
            max-width: 720px;
            width: 100%;
            margin: 0 !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-width: 350px;
                        `,
                        css`
                            max-width: 350px;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
};

const defaultCustomCss = `
.${fields.cssClass}{
}
`;

const styles = heroBannerStyles;

export const HomePageTopBannerImageWidget: FC<OwnProps> = ({ fields }) => {
    const overlayWrapperStyles = {
        css: css`
            ${styles.overlayWrapper?.css || ""}
            background-image: url(${fields.image});
            background-repeat: no-repeat;
            width: 100%;
            height: auto;
            aspect-ratio: 2/1;
            object-fit: cover;
            background-size: cover;
            display: flex;
            justify-content: ${fields.verticalTextAlignment};
            align-items: center;
            flex-direction: column;
        `,
    };
    const MobileBackground = {
        css: css`
            ${styles.overlayWrapper?.css || ""}
            background-image: url(${fields.mobileImage});
            background-repeat: no-repeat;
            width: 100%;
            height: auto;
            aspect-ratio: 1/1;
            object-fit: cover;
            background-size: contain;
            display: flex;
            justify-content: ${fields.mobileVerticalAlignment};
            align-items: center;
            flex-direction: column;
        `,
    };

    const slideOverlayWrapperStyles = {
        css: css`
            display: flex;
            justify-content: center;
            align-items: ${fields.horizontalTextAlignment};
            flex-direction: column;
            width: 100%;
            padding: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 20px;
                        `,
                        css`
                            padding: 20px;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    };

    const mobileSlideOverlayWrapperStyles = {
        css: css`
            display: flex;
            justify-content: center;
            align-items: ${fields.mobileHorizontalAlignment};
            flex-direction: column;
            width: 100%;
            padding: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 20px;
                        `,
                        css`
                            padding: 20px;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    };

    const { url, title } = useGetLink(fields.buttonLink);
    const onClick = (openInNewWindow: boolean) => {
        if (!url) {
            return;
        }
        if (openInNewWindow) {
            window.open(url, "_blank");
        } else {
            window.location.href = url;
        }
    };

    const bannerButton = {
        css: css`
            cursor: pointer;
            margin-top: 15px;
            padding: 0px 25.5px;
            background: ${fields.buttonBackgroundColor};
            border-radius: 0px;
            border-color: ${fields.buttonBackgroundColor};
            height: 40px;
            &:hover {
                background: ${fields.buttonBackgroundColor};
                border-color: ${fields.buttonBackgroundColor};
                opacity: ${fields.opacity};
            }
            > span {
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-weight: 700;
                color: ${fields.buttonTextColor};
                font-size: 15px;
                line-height: 24px;
            }
        `,
    };

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <>
            <Hidden below="md">
                <StyledWrapper
                    className="banner-wrapper"
                    {...styles.heroBannerImageContainer}
                    onClick={() => onClick(fields.openInNewWindow)}
                >
                    <StyledWrapper className="banner-overlay-wrapper" {...overlayWrapperStyles}>
                        <StyledWrapper {...slideOverlayWrapperStyles}>
                            {fields.heading && (
                                <StyledWrapper {...styles.container}>
                                    <Typography {...styles.heading} role="heading">
                                        {parse(fields.heading, parserOptions)}
                                    </Typography>
                                    <StyledWrapper {...customCssWrapper}>
                                        {fields.buttonLabel && (
                                            <Button
                                                {...bannerButton}
                                                className={fields.cssClass}
                                                onClick={() => onClick(fields.openInNewWindow)}
                                            >
                                                {fields.buttonLabel}
                                            </Button>
                                        )}
                                    </StyledWrapper>
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </Hidden>
            <Hidden above="sm">
                <StyledWrapper {...styles.heroBannerImageContainer} onClick={() => onClick(fields.openInNewWindow)}>
                    <StyledWrapper className="banner-overlay-wrapper" {...MobileBackground}>
                        <StyledWrapper {...mobileSlideOverlayWrapperStyles}>
                            {fields.heading && (
                                <StyledWrapper {...styles.container}>
                                    <Typography {...styles.heading} role="heading">
                                        {parse(fields.heading, parserOptions)}
                                    </Typography>
                                    <StyledWrapper {...customCssWrapper}>
                                        {fields.buttonLabel && (
                                            <Button
                                                {...bannerButton}
                                                className={fields.cssClass}
                                                onClick={() => onClick(fields.openInNewWindow)}
                                            >
                                                {fields.buttonLabel}
                                            </Button>
                                        )}
                                    </StyledWrapper>
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </Hidden>
        </>
    );
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const homePageBanner: WidgetModule = {
    component: HomePageTopBannerImageWidget,
    definition: {
        group: "Basic",
        icon: "image",
        displayName: "SurHomePageTopBannerWidget",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.image,
                displayName: "Image for Desktop",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.mobileImage,
                displayName: "Image for Mobile",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.heading,
                editorTemplate: "RichTextField",
                displayName: "Heading",
                defaultValue: "",
            },
            {
                name: fields.buttonBackgroundColor,
                displayName: "Button Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "#FFFFFF",
                fieldType: "General",
            },
            {
                fieldType: "General",
                name: fields.buttonLabel,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.buttonTextColor,
                displayName: "Button Text Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "#323132",
                fieldType: "General",
            },
            {
                name: fields.opacity,
                displayName: "Opacity",
                editorTemplate: "TextField",
                defaultValue: "1",
                fieldType: "General",
            },

            {
                fieldType: "General",
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.openInNewWindow,
                fieldType: "General",
                displayName: "Open in New Window",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                name: fields.horizontalTextAlignment,
                editorTemplate: "DropDownField",
                defaultValue: "center",
                fieldType: "General",
                options: [
                    { displayName: "Left", value: "flex-start; text-align: left" },
                    { displayName: "Center", value: "center; text-align: center" },
                    { displayName: "Right", value: "flex-end; text-align: right" },
                ],
            },
            {
                name: fields.verticalTextAlignment,
                editorTemplate: "DropDownField",
                defaultValue: "center",
                fieldType: "General",
                options: [
                    { displayName: "Top", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Bottom", value: "flex-end" },
                ],
            },
            {
                name: fields.mobileHorizontalAlignment,
                editorTemplate: "DropDownField",
                defaultValue: "center",
                fieldType: "General",
                options: [
                    { displayName: "Left", value: "flex-start; text-align: left" },
                    { displayName: "Center", value: "center; text-align: center" },
                    { displayName: "Right", value: "flex-end; text-align: right" },
                ],
            },
            {
                name: fields.mobileVerticalAlignment,
                editorTemplate: "DropDownField",
                defaultValue: "center",
                fieldType: "General",
                options: [
                    { displayName: "Top", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Bottom", value: "flex-end" },
                ],
            },
            {
                name: fields.cssClass,
                displayName: "Add CSS Class",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default homePageBanner;
